import { Component, DestroyRef, OnInit, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { AVAILABLE_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { ApiUsersRepo } from '@client/app/feature-modules/admin/modules/admin-api-tokens/services/api-users.repo';
import { ApiUser, ApiUserScope } from '@client/app/feature-modules/admin/modules/admin-api-tokens/types/api-user.type';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { OauthService } from '@monsido/oauth/oauth.service';
import { proxyApiPath } from '@monsido/core/constants/general.constant';
import {
    CopyToClipboardButtonComponent,
} from '@client/app/shared-modules/clipboard/components/copy-to-clipboard-button/copy-to-clipboard-button/copy-to-clipboard-button.component';
import { MonEventTriggerModule } from '@monsido/angular-shared-components';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { ParamService } from '@monsido/core/param/param.service';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import {
    AddApiTokenDialogComponent, AddApiTokenDialogComponentModel,
} from '@client/app/feature-modules/admin/modules/admin-api-tokens/components/add-api-token-dialog/add-api-token-dialog/add-api-token-dialog.component';
import { ApiTokensService } from '@client/app/feature-modules/admin/modules/admin-api-tokens/services/api-tokens.service';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { Menu } from 'primeng/menu';
import { TableWrapperComponent } from '@client/app/shared-modules/table/components/table-wrapper.component';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';

type ComputedApiToken = {
    id: number
    description: string
    scopes: string
    token: string
    actionItems: MenuItem[]
};

@Component({
    selector: 'mon-admin-admin-api-tokens',
    templateUrl: './admin-api-tokens.component.html',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        TranslocoModule,
        CopyToClipboardButtonComponent,
        MonEventTriggerModule,
        TooltipModule,
        ConfirmDialog,
        PageLayoutComponent,
        Menu,
        TableWrapperComponent,
        TableCellTemplateDirective,
    ],
    providers: [
        ConfirmationService,
        DialogService,
        ApiTokensService,
    ],
    styleUrls: ['./admin-api-tokens.component.scss'],
})
export class AdminApiTokensComponent implements OnInit {
    private readonly translocoService = inject(TranslocoService);

    readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;

    readonly TABLE_SCHEMA = TableWrapperComponent.createTableSchema<ComputedApiToken>({
        API_URL: {
            label: this.translocoService.translate('adminApiTokens.table.header.apiUrl'),
            id: 'apiUrl',
            type: 'custom',
        },
        DESCRIPTION: {
            label: this.translocoService.translate('adminApiTokens.table.header.description'),
            id: 'description',
            type: 'property',
        },
        SCOPES: {
            label: this.translocoService.translate('adminApiTokens.table.header.scope'),
            id: 'scopes',
            type: 'property',
        },
        TOKEN: {
            label: this.translocoService.translate('adminApiTokens.table.header.token'),
            id: 'token',
            type: 'custom',
        },
        ACTIONS: {
            label: this.translocoService.translate('adminApiTokens.table.header.actions'),
            id: 'actions',
            type: 'custom',
            actionColumn: true,
        },
    });

    @ViewChild(TableWrapperComponent) private readonly tableWrapper?: TableWrapperComponent<ComputedApiToken>;

    private menuTriggerElement: HTMLElement | null = null;

    apiTokens: ComputedApiToken[] = [];
    currentPage = 1;
    perPage = AVAILABLE_PAGE_SIZE[0];
    total = 0;
    apiPath = '';
    loading = true;

    constructor (
        private readonly apiTokensRepo: ApiUsersRepo,
        private readonly oauthService: OauthService,
        private readonly actionTriggerService: ActionTriggerService,
        private readonly paramService: ParamService,
        private readonly confirmationService: ConfirmationService,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly dialogService: DialogService,
        private readonly apiTokensService: ApiTokensService,
        private readonly destroyRef: DestroyRef,
        private readonly adminLayoutService: AdminLayoutService,
    ) {}

    ngOnInit (): void {
        this.setBreadcrumbs();
        this.apiPath = proxyApiPath + '/api';
        const { page_size, page } = this.paramService.getParams();
        this.getApiTokens(
            parseInt(page),
            parseInt(page_size),
        ).then();

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((params) => {
            this.getApiTokens(
                parseInt(params.page),
                parseInt(params.page_size),
            ).then();
        });

        this.destroyRef.onDestroy(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            deregisterHook();
        });
    }

    setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('admin.apiTokens.table.breadcrumbLabel'),
                    state: 'base.customer.admin.api-users',
                },
            ],
        });
    }

    async getApiTokens (page: number, pageSize: number): Promise<void> {
        this.loading = true;
        const apiTokensResponse = await this.apiTokensRepo.getAll({
            page: page,
            page_size: pageSize,
        });

        if (!apiTokensResponse) {
            return;
        }

        this.perPage = apiTokensResponse.perPage || 0;
        this.currentPage = apiTokensResponse.currentPage || 1;
        this.total = apiTokensResponse.total || 0;

        this.apiTokens = apiTokensResponse.map(apiToken => {
            const computedApiToken: ComputedApiToken = {
                id: apiToken.id,
                description: apiToken.description,
                scopes: apiToken.scopes.map(scope => this.getScopeTranslation(scope)).join(', '),
                token: apiToken.token,
                actionItems: [
                    {
                        label: this.translocoService.translate('adminApiTokens.table.deleteApiTokenButton.ariaLabel'),
                        icon: 'pi pi-trash',
                        command: () => this.deleteApiToken(apiToken),
                    },
                    {
                        label: this.translocoService.translate('adminApiTokens.table.revokeApiTokenButton.ariaLabel'),
                        icon: 'pi pi-undo',
                        command: () => this.revokeApiToken(apiToken),
                    },
                ] as MenuItem[],
            };
            return computedApiToken;
        });
        this.loading = false;
    }

    async reload (): Promise<void> {
        const { page_size, page } = this.paramService.getParams();
        return this.getApiTokens(
            parseInt(page),
            parseInt(page_size),
        );
    }

    onPageChange (pageChangeEvent: TablePageEvent) {
        const { first, rows } = pageChangeEvent;

        // Check if page size has changed
        const pageSizeChanged = rows !== this.perPage;

        // If page size changed, reset to page 1; otherwise calculate current page
        const pageNumber = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;

        this.paramService.updateParams({
            page: pageNumber,
            page_size: rows,
        });
    }

    addApiToken (): void {
        const dialogRef = this.dialogService.open(AddApiTokenDialogComponent, {
            header: this.translocoService.translate('adminApiTokens.addApiTokenDialog.header'),
            baseZIndex: 10000,
            showHeader: true,
            closable: true,
            dismissableMask: true,
            width: '800px',
        });

        dialogRef.onClose.subscribe(async (result: AddApiTokenDialogComponentModel | null) => {
            if (!result) {
                return;
            }

            this.loading = true;
            const apiToken = await this.apiTokensRepo.create({
                description: result.description,
                scopes: result.scopes,
            });

            if (!apiToken) {
                return;
            }

            this.globalToastMessageService.success(
                this.translocoService.translate('adminApiTokens.addApiTokenToast.success'),
            );

            await this.reload();
        });
    }

    public onMenuHide (): void {
        const el = document.activeElement;
        this.menuTriggerElement = (el instanceof HTMLElement) ? el : null;
    }

    deleteApiToken (apiToken: ApiUser): void {
        this.tableWrapper?.trackAction(this.apiTokens, this.apiTokens.find(t => t.id === apiToken.id) ?? apiToken as unknown as ComputedApiToken, 'delete', () => true);
        this.actionTriggerService.track({ action: 'admin-apiTokens-delete-api-token-btn' });
        this.confirmationService.confirm({
            message: this.translocoService.translate('adminApiTokens.deleteApiTokenDialog.description'),
            icon: 'pi pi-exclamation-triangle',
            header: this.translocoService.translate('adminApiTokens.deleteApiTokenDialog.header'),
            closable: true,
            closeOnEscape: true,
            dismissableMask: true,
            rejectButtonProps: {
                label: this.translocoService.translate('adminApiTokens.deleteApiTokenDialog.rejectButtonLabel'),
                severity: 'secondary',
            },
            acceptButtonProps: {
                label: this.translocoService.translate('adminApiTokens.deleteApiTokenDialog.acceptButtonLabel'),
                severity: 'primary',
            },
            reject: () => {
                this.tableWrapper?.focusService?.reset();
                this.menuTriggerElement?.focus();
                this.menuTriggerElement = null;
            },
            accept: async () => {
                this.loading = true;
                await this.apiTokensRepo.destroy({
                    id: apiToken.id,
                });
                this.globalToastMessageService.info(
                    this.translocoService.translate('adminApiTokens.deleteApiTokenToast.success'),
                );
                await this.reload();
            },
        });
    }

    revokeApiToken (apiToken: ApiUser): void {
        this.tableWrapper?.trackAction(this.apiTokens, this.apiTokens.find(t => t.id === apiToken.id) ?? apiToken as unknown as ComputedApiToken, 'revoke', () => true);
        this.actionTriggerService.track({ action: 'admin-apiTokens-revoke-api-token-btn' });
        this.confirmationService.confirm({
            message: `${this.translocoService.translate('adminApiTokens.revokeApiTokenDialog.description')}<br>
            ${this.translocoService.translate('adminApiTokens.revokeApiTokenDialog.subDescription')}`,
            icon: 'pi pi-exclamation-triangle',
            header: this.translocoService.translate('adminApiTokens.revokeApiTokenDialog.header'),
            closable: true,
            closeOnEscape: true,
            dismissableMask: true,
            rejectButtonProps: {
                label: this.translocoService.translate('adminApiTokens.revokeApiTokenDialog.rejectButtonLabel'),
                severity: 'secondary',
            },
            acceptButtonProps: {
                label: this.translocoService.translate('adminApiTokens.revokeApiTokenDialog.acceptButtonLabel'),
                severity: 'primary',
            },
            reject: () => {
                this.tableWrapper?.focusService?.reset();
                this.menuTriggerElement?.focus();
                this.menuTriggerElement = null;
            },
            accept: async () => {
                this.loading = true;
                await this.apiTokensRepo.revokeToken({
                    id: apiToken.id,
                });
                this.globalToastMessageService.success(
                    this.translocoService.translate('adminApiTokens.revokeApiTokenToast.success'),
                );
                await this.reload();
            },
        });
    }

    private getScopeTranslation (scope: ApiUserScope): string {
        const scopeTranslationMap = this.apiTokensService.getScopesTranslationMap();
        return scopeTranslationMap.get(scope) || '';
    }


}
